import { useEffect, useState, useRef } from "react";
import AudioPlayer from "../../../components/Ielts_test/audioPlayer/audioPlayer";

import QuestionItem from "../../../components/Ielts_test/soal/listening/QuestionItem";
import ResultModal from "../../../components/Ielts_test/hasil/listening/ResultModal";
import { useNavigate } from "react-router-dom";
import {
  getIetlsListeningQuestions,
  submitIetlsListeningAnswer,
} from "../../../hooks/useApi";
import { useAudioPlayerContext } from "../../../context/audioPlayerContext";

// Helper to order results based on question order
function normalizeIeltsResult(detailAnswers, questionOrderMap) {
  return detailAnswers
    .map((ans) => {
      const order = questionOrderMap[ans.question_id];

      return {
        ...ans,
        number: order?.number ?? null,
        partIndex: order?.partIndex ?? null,
        sectionIndex: order?.sectionIndex ?? null,
        questionIndex: order?.questionIndex ?? null,
      };
    })
    .sort((a, b) => a.number - b.number);
}

function countContextQuestions(context) {
  return context.sections.reduce(
    (sum, section) => sum + section.questions.length,
    0,
  );
}

function getContextStartIndex(contexts, currentContextIdx) {
  return contexts
    .slice(0, currentContextIdx)
    .reduce((sum, context) => sum + countContextQuestions(context), 0);
}

export default function ListeningSoalPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [allContexts, setAllContexts] = useState([]);
  const questionRefs = useRef([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentContextIdx, setCurrentContextIdx] = useState(0);
  const [duration, setDuration] = useState(0);
  const [result, setResult] = useState(null);
  const [isRunning, setIsRunning] = useState(true);
  const [startTime] = useState(Date.now());
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { setCurrentAudio } = useAudioPlayerContext();

  const currentContext = allContexts[currentContextIdx];
  const totalAnswered = Object.keys(answers).length;

  useEffect(() => {
    if (currentContext?.sound_url) {
      setCurrentAudio(currentContext.sound_url);
    }
  }, [currentContext, setCurrentAudio]);

  // Timer
  useEffect(() => {
    if (!isRunning) return;

    const timer = setInterval(() => {
      setDuration(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, [startTime, isRunning]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getIetlsListeningQuestions();
        // The API hook already returns normalized data
        const normalized = res;
        setData(normalized);

        // Flatten all contexts from all parts into a single array
        const contexts = normalized.parts.flatMap((part) =>
          part.contexts.map((context) => ({
            ...context,
            sound_url: part.sound_url,
          })),
        );
        setAllContexts(contexts);

        // Initialize refs for all questions across all contexts
        // We need as many refs as the total number of questions
        const totalQuestions = contexts.reduce(
          (sum, context) => sum + countContextQuestions(context),
          0,
        );
        questionRefs.current = new Array(totalQuestions).fill(null);
      } catch (error) {
        console.error(error);
        navigate("/listening-test");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleAnswer = (answer) => {
    setAnswers((prev) => ({ ...prev, [answer.question_id]: answer }));
  };

  const scrollToQuestion = (globalIndex) => {
    questionRefs.current[globalIndex]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  const handleSubmit = async () => {
    const total = data.parts.flatMap((p) =>
      p.contexts.flatMap((context) =>
        context.sections.flatMap((section) => section.questions),
      ),
    ).length;
    const answered = Object.keys(answers).length;
    if (answered < total) {
      setShowConfirm(true);
      return;
    }
    await submitAnswers();
  };

  const submitAnswers = async () => {
    setShowConfirm(false);
    setSubmitting(true);
    try {
      const payload = Object.values(answers).map((ans) => ({
        ...ans,
        ...(typeof ans.answer === "boolean" && { answer: String(ans.answer) }),
      }));
      const res = await submitIetlsListeningAnswer(payload);
      console.log("Submit response:", res);
      setIsRunning(false);

      const orderedDetailAnswers = normalizeIeltsResult(
        res.details,
        data.questionOrderMap,
      );

      setResult({
        ...res,
        detail_answers: orderedDetailAnswers,
      });
    } catch (error) {
      alert(error.message || "Gagal mengumpulkan jawaban");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-screen">
        <span className="loading loading-spinner loading-lg text-primary"></span>
      </div>
    );

  if (!data || allContexts.length === 0) return null;

  // Calculate the global starting index for questions in the current context
  // This is needed to map local context question index to the global `questionRefs` array
  const currentContextStartGlobalIdx = getContextStartIndex(
    allContexts,
    currentContextIdx,
  );

  return (
    <>
      <AudioPlayer />
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="bg-white rounded-t-xl shadow-md p-4 mb-2 text-center">
          <h1 className="font-anton text-2xl md:text-3xl text-primary">
            Listening
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {currentContext.context_title}
          </p>
          <p className="text-sm text-gray-600 mt-1">
            Listen the instruction and answer the questions{" "}
            {currentContext.start_number}–{currentContext.end_number}
          </p>
        </div>

        {/* Current Context Questions */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow-md p-5 max-h-[70vh] overflow-y-auto space-y-8">
            <div className="space-y-6">
              {currentContext.sections.map((section, sectionIdx) => {
                const sectionStartGlobalIdx =
                  currentContextStartGlobalIdx +
                  currentContext.sections
                    .slice(0, sectionIdx)
                    .reduce((sum, sec) => sum + sec.questions.length, 0);

                return (
                  <div key={section.instruksi_id} className="space-y-4">
                    {/* Instruction Block (IELTS style) */}
                    <div className="bg-gray-100 border-l-4 border-primary p-4 rounded">
                      <p className="font-anton text-primary">
                        Questions {section.start_number}–{section.end_number}
                      </p>
                      <p className="font-poppins text-sm text-gray-700 mt-1">
                        {section.instruksi_title}
                      </p>
                    </div>

                    {/* Questions */}
                    {section.questions.map((q, idx) => {
                      const globalIdx = sectionStartGlobalIdx + idx;

                      return (
                        <div
                          key={q.id}
                          ref={(el) => (questionRefs.current[globalIdx] = el)}
                          className="scroll-mt-24"
                        >
                          <QuestionItem
                            question={q}
                            onAnswer={handleAnswer}
                            userAnswer={answers[q.id]}
                          />
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Number Navigation (Current Context Only) */}
        <div className="mt-6 w-full flex justify-center">
          <div className="bg-gray-50 rounded-xl p-4 shadow-inner max-w-3xl w-full">
            <div className="text-center text-xs text-gray-500 mb-2 font-poppins">
              Questions {currentContext.start_number}–
              {currentContext.end_number}
            </div>
            <div className="flex flex-wrap gap-2 justify-center">
              {currentContext.sections.flatMap((section, sectionIdx) => {
                const sectionStartGlobalIdx =
                  currentContextStartGlobalIdx +
                  currentContext.sections
                    .slice(0, sectionIdx)
                    .reduce((sum, sec) => sum + sec.questions.length, 0);

                return section.questions.map((q, idx) => {
                  const globalIdx = sectionStartGlobalIdx + idx;
                  const answered = answers[q.id];

                  return (
                    <button
                      key={q.id}
                      onClick={() => scrollToQuestion(globalIdx)}
                      className={`
                        w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold
                        ${answered ? "bg-accent text-white" : "bg-white border border-gray-300"}
                      `}
                    >
                      {q.number}
                    </button>
                  );
                });
              })}
            </div>
          </div>
        </div>

        {/* Footer Navigation */}
        <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
          <button
            onClick={() =>
              setCurrentContextIdx(Math.max(0, currentContextIdx - 1))
            }
            disabled={currentContextIdx === 0}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-xl font-poppins disabled:opacity-50 hover:bg-gray-300 w-full sm:w-auto"
          >
            Previous 
          </button>

          <div className="text-sm font-poppins text-gray-600">
            {totalAnswered} / {data.total_questions || "40"} answered
          </div>

          {currentContextIdx < allContexts.length - 1 ? (
            <button
              onClick={() => setCurrentContextIdx(currentContextIdx + 1)}
              className="px-6 py-2 bg-primary text-white rounded-xl font-poppins font-semibold hover:bg-secondary w-full sm:w-auto"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-8 py-2 bg-accent text-primary rounded-xl font-poppins font-bold hover:scale-105 disabled:opacity-50 w-full sm:w-auto"
            >
              {submitting ? "Submitting..." : "Submit All"}
            </button>
          )}
        </div>
      </div>
      {/* Result Modal */}
      {result && (
        <ResultModal
          result={result}
          data={data}
          duration={duration}
          onClose={() => navigate("/IELTSTest")}
          test_type="Listening"
        />
      )}
    </>
  );
}
